import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/src/components/ui/select";
import { Template } from "@/src/types/template.zod";
import { useTranslation } from "@/src/i18n/client";
import { Label } from "@/src/components/ui/label";

interface TemplateSelectProps {
  value: string;
  onValueChange: (value: string) => void;
  templates: Template[];
  isLoading: boolean;
}

export function TemplateSelect({
  value,
  onValueChange,
  templates,
  isLoading
}: TemplateSelectProps) {
  const { t } = useTranslation();
  
  // Find the selected template to display its label
  const selectedTemplate = templates.find(template => template.id === value);
  const displayValue = selectedTemplate ? (selectedTemplate.description ?? selectedTemplate.name) : undefined;
  
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
     <Label
        htmlFor="template-select"
        className="block text-sm font-medium text-gray-900 dark:text-white mb-0 w-full sm:w-28"
      >
        {t('homePage.templateSelect.label')}
      </Label>
      <div className="w-full sm:flex-1 min-w-0">
        <Select
          value={value}
          onValueChange={(value) => value && onValueChange(value)}
          disabled={isLoading || templates.length === 0}
        >
          <SelectTrigger
            id="template-select"
            className="w-full min-w-0 rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm focus:border-primary focus:ring-2 focus:ring-primary/20 dark:bg-background dark:text-foreground dark:border-input"
          >
            {displayValue || <span className="text-muted-foreground">{isLoading ? t('homePage.templateSelect.loading') : t('homePage.templateSelect.placeholder')}</span>}
          </SelectTrigger>
          <SelectContent
            className="rounded-md border border-input bg-background shadow-lg dark:bg-background dark:border-input"
          >
            {templates.map((template) => (
              <SelectItem key={template.id} value={template.id} className="px-3 py-2 hover:bg-primary/10 dark:hover:bg-primary/20">
                {template.description ?? template.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
